import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { LogoGenerator } from '@/components/LogoGenerator';
import { ImageEditor } from '@/components/ImageEditor';
import { DesignAssistant } from '@/components/DesignAssistant';
import { ProductCustomizer } from '@/components/ProductCustomizer';
import Navigation from '@/components/Navigation';
import { Footer } from '@/components/branding';
import { Sparkles, ImageIcon, MessageSquare, Package } from 'lucide-react';
import { isGeminiConfigured } from '@/services/geminiService';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

const APIKeyWarning: React.FC = () => (
  <Alert variant="destructive" className="mb-6">
    <AlertTriangle className="h-4 w-4" />
    <AlertTitle>API Key Not Configured</AlertTitle>
    <AlertDescription>
      The Google Gemini API key is not configured. Please add{' '}
      <code className="bg-red-100 px-1 py-0.5 rounded">VITE_GOOGLE_AI_API_KEY</code> to your{' '}
      <code className="bg-red-100 px-1 py-0.5 rounded">.env.local</code> file.
    </AlertDescription>
  </Alert>
);

export default function AIStudioPage() {
  const isConfigured = isGeminiConfigured();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <Navigation />

      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              AI-Powered Design Studio
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
              Create Amazing Designs
            </span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Generate custom logos, edit images, get design advice, and apply your creations to
            products - all powered by Google Gemini AI
          </p>
        </div>

        {/* API Key Warning */}
        {!isConfigured && <APIKeyWarning />}

        {/* Main Tabs */}
        <Card className="shadow-2xl border-0 overflow-hidden">
          <Tabs defaultValue="logo" className="w-full">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-1">
              <TabsList className="w-full bg-white/95 backdrop-blur grid grid-cols-2 md:grid-cols-4 h-auto p-2 gap-2">
                <TabsTrigger
                  value="logo"
                  className="flex flex-col md:flex-row items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white"
                >
                  <Sparkles className="h-5 w-5" />
                  <span className="font-medium">Logo Generator</span>
                </TabsTrigger>

                <TabsTrigger
                  value="editor"
                  className="flex flex-col md:flex-row items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white"
                >
                  <ImageIcon className="h-5 w-5" />
                  <span className="font-medium">Image Editor</span>
                </TabsTrigger>

                <TabsTrigger
                  value="assistant"
                  className="flex flex-col md:flex-row items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span className="font-medium">AI Assistant</span>
                </TabsTrigger>

                <TabsTrigger
                  value="customizer"
                  className="flex flex-col md:flex-row items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white"
                >
                  <Package className="h-5 w-5" />
                  <span className="font-medium">Customizer</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab Content */}
            <div className="p-0">
              <TabsContent value="logo" className="m-0 focus-visible:ring-0">
                <LogoGenerator />
              </TabsContent>

              <TabsContent value="editor" className="m-0 focus-visible:ring-0">
                <ImageEditor />
              </TabsContent>

              <TabsContent value="assistant" className="m-0 focus-visible:ring-0">
                <DesignAssistant />
              </TabsContent>

              <TabsContent value="customizer" className="m-0 focus-visible:ring-0">
                <ProductCustomizer />
              </TabsContent>
            </div>
          </Tabs>
        </Card>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Sparkles className="h-10 w-10 text-purple-600 mb-4" />
            <h3 className="font-bold text-lg mb-2">AI Logo Generation</h3>
            <p className="text-sm text-muted-foreground">
              Create unique logos from text descriptions using advanced AI models
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <ImageIcon className="h-10 w-10 text-pink-600 mb-4" />
            <h3 className="font-bold text-lg mb-2">Smart Editing</h3>
            <p className="text-sm text-muted-foreground">
              Edit images with simple text commands - remove backgrounds, enhance quality
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <MessageSquare className="h-10 w-10 text-purple-600 mb-4" />
            <h3 className="font-bold text-lg mb-2">Design Assistant</h3>
            <p className="text-sm text-muted-foreground">
              Get expert advice on colors, styles, and printing best practices
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <Package className="h-10 w-10 text-pink-600 mb-4" />
            <h3 className="font-bold text-lg mb-2">Product Preview</h3>
            <p className="text-sm text-muted-foreground">
              See your designs on real products before ordering
            </p>
          </Card>
        </div>

        {/* How It Works */}
        <div className="mt-16 text-center space-y-8">
          <h2 className="text-3xl font-bold">How It Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center text-xl font-bold mx-auto">
                1
              </div>
              <h3 className="font-semibold text-lg">Create Your Design</h3>
              <p className="text-sm text-muted-foreground">
                Generate a logo with AI or upload and edit your own images
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center text-xl font-bold mx-auto">
                2
              </div>
              <h3 className="font-semibold text-lg">Customize & Preview</h3>
              <p className="text-sm text-muted-foreground">
                Apply your design to products and adjust size, position, and colors
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-center text-xl font-bold mx-auto">
                3
              </div>
              <h3 className="font-semibold text-lg">Order & Enjoy</h3>
              <p className="text-sm text-muted-foreground">
                Add to cart and we'll print your custom design on high-quality products
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}